import React, { useState } from 'react';
import { motion, useMotionValue, useTransform, animate, PanInfo } from 'framer-motion';
import { PhotoData } from '../data/photos';
interface PolaroidProps {
  photo: PhotoData;
  isTop: boolean;
  stackPosition: number;
  onSwipe: () => void;
  onInteract: () => void;
}
export const Polaroid: React.FC<PolaroidProps> = ({
  photo,
  isTop,
  stackPosition,
  onSwipe,
  onInteract
}) => {
  const [exitDirection, setExitDirection] = useState({
    x: 0,
    y: 0,
    rotate: 0
  });
  const [isExiting, setIsExiting] = useState(false);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  // Rotation while dragging — based on horizontal motion
  const rotate = useTransform(x, [-250, 250], [photo.rotation - 16, photo.rotation + 16]);
  const handleDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const distance = Math.sqrt(info.offset.x * info.offset.x + info.offset.y * info.offset.y);
    const velocityMag = Math.sqrt(info.velocity.x * info.velocity.x + info.velocity.y * info.velocity.y);
    const distanceThreshold = 100;
    const velocityThreshold = 400;
    if (distance > distanceThreshold || velocityMag > velocityThreshold) {
      // Determine throw direction (use velocity if strong, fall back to offset)
      let dirX = info.velocity.x;
      let dirY = info.velocity.y;
      if (Math.abs(dirX) < 50 && Math.abs(dirY) < 50) {
        dirX = info.offset.x;
        dirY = info.offset.y;
      }
      const mag = Math.sqrt(dirX * dirX + dirY * dirY) || 1;
      const screenDiagonal = Math.sqrt(window.innerWidth ** 2 + window.innerHeight ** 2) + 400;
      setExitDirection({
        x: dirX / mag * screenDiagonal,
        y: dirY / mag * screenDiagonal,
        rotate: dirX / mag * 35
      });
      setIsExiting(true);
      onSwipe();
    } else {
      // Not enough — gently spring the card back to center
      animate(x, 0, {
        type: 'spring',
        stiffness: 200,
        damping: 26,
        mass: 1
      });
      animate(y, 0, {
        type: 'spring',
        stiffness: 200,
        damping: 26,
        mass: 1
      });
    }
  };
  // Stack visual properties
  const scale = 1 - stackPosition * 0.04;
  const yOffset = stackPosition * 10;
  const zIndex = 40 - stackPosition;
  return <div className="absolute top-1/2 left-1/2" style={{
    transform: 'translate(-50%, -50%)',
    zIndex,
    pointerEvents: isTop && !isExiting ? 'auto' : 'none'
  }} data-id="element-68">
      <motion.div className="bg-cream pt-3 px-3 pb-[70px] rounded-[3px] flex flex-col select-none" style={{
      width: 'min(90vw, 360px)',
      x: isTop ? x : 0,
      y: isTop ? y : undefined,
      rotate: isTop ? rotate : photo.rotation,
      touchAction: 'none',
      cursor: isTop ? 'grab' : 'default',
      boxShadow: '0 12px 28px -8px rgba(0, 0, 0, 0.5), 0 6px 12px -4px rgba(0, 0, 0, 0.35)'
    }} initial={{
      scale: 0.92,
      opacity: 0
    }} animate={isTop ? {
      scale: 1,
      opacity: 1
    } : {
      scale,
      y: yOffset,
      opacity: 1 - stackPosition * 0.08
    }} exit={{
      x: exitDirection.x,
      y: exitDirection.y,
      rotate: photo.rotation + exitDirection.rotate,
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.9,
        ease: [0.22, 0.61, 0.36, 1]
      }
    }} transition={{
      type: 'spring',
      stiffness: 120,
      damping: 22,
      mass: 1
    }} drag={isTop && !isExiting} dragElastic={1} dragMomentum={false} onDragStart={onInteract} onDragEnd={handleDragEnd} whileDrag={{
      cursor: 'grabbing'
    }} data-id="element-69">
        {/* Photo Area */}
        <div className="w-full aspect-square bg-[#1A2F4F]/10 overflow-hidden relative pointer-events-none" data-id="element-70">
          <img src={photo.src} alt={photo.note} className="w-full h-full object-cover" draggable={false} data-id="element-71" />
          <div className="absolute inset-0 shadow-[inset_0_0_12px_rgba(0,0,0,0.15)]" data-id="element-72" />
        </div>

        {/* Handwritten Note Area */}
        <div className="absolute bottom-0 left-0 right-0 h-[70px] flex items-center justify-center px-4 pointer-events-none" data-id="element-73">
          <p className="font-marker font-bold text-royal-blue text-xl sm:text-2xl text-center leading-tight" style={{
          transform: 'rotate(-2deg)'
        }} data-id="element-74">
            {photo.note}
          </p>
        </div>
      </motion.div>
    </div>;
};